/**
 * Feature flags — resolved at runtime from URL querystring + localStorage.
 *
 * jackpotGoldColors
 *   Renders jackpot insight heat-map tiles using 3 gold shades (#FFD700 → #DAA520 → #B8860B)
 *   instead of the standard pink/warm color scale.
 *
 *   Control via querystring:
 *     ?jackpotcolor=gold   — use gold shades (persisted to localStorage)
 *     ?jackpotcolor=pink   — revert to standard colors (persisted to localStorage)
 *
 *   Resolution order (highest priority first):
 *     1. querystring value on current page load (also written to localStorage)
 *     2. value previously stored in localStorage
 *     3. default: true (gold shades)
 *
 * hotColdShortcuts
 *   Shows the "Hottest Slots" and "Coldest Slots" shortcut bubbles above the map.
 *
 *   Control via querystring:
 *     ?mapshortcuts=enablehotcold   — show hot/cold shortcuts (persisted to localStorage)
 *     ?mapshortcuts=disablehotcold  — hide hot/cold shortcuts (persisted to localStorage)
 *
 *   Resolution order (highest priority first):
 *     1. querystring value on current page load (also written to localStorage)
 *     2. value previously stored in localStorage
 *     3. default: false (hidden)
 *
 * drawerActionButtons
 *   The EGM action pills (Selected / Compare / Add to Favorites / Add to Playlist)
 *   floating above the drawer, plus the casino-scoped Selected-EGMs working set and
 *   the two-tap EGM-name flow. This is now the DEFAULT app behavior; the flag remains
 *   only as a rollback kill-switch.
 *
 *   Control via querystring (note: this flag uses sessionStorage, not localStorage,
 *   so it is scoped to the browser tab/session):
 *     ?featureFlagDrawerActionButtons=false  — disable (kill-switch, persisted to sessionStorage)
 *     ?featureFlagDrawerActionButtons=true   — re-enable (clears the kill-switch)
 *
 *   Resolution order (highest priority first):
 *     1. querystring value on current page load (false writes the kill-switch, true clears it)
 *     2. value previously stored in sessionStorage
 *     3. default: true (on)
 *
 * nearbyCasinos
 *   "Find casinos near me" on the casino selection screen (ChooseCasinos): asks for
 *   the device location, shows a map of nearby casinos and sorts the list by
 *   distance. Off by default; the current casino list is unchanged until enabled.
 *
 *   Control via querystring:
 *     ?nearbycasinos=on    — enable (persisted to localStorage)
 *     ?nearbycasinos=off   — disable (persisted to localStorage)
 *
 *   Resolution order (highest priority first):
 *     1. querystring value on current page load (also written to localStorage)
 *     2. value previously stored in localStorage
 *     3. default: false (off)
 */

const JACKPOT_COLOR_KEY = 'sc_jackpot_color';
const MAP_SHORTCUTS_KEY = 'sc_map_shortcuts';
const DRAWER_ACTION_BUTTONS_KEY = 'sc_drawer_action_buttons';

const resolveJackpotGoldColors = (): boolean => {
  try {
    const qs = new URLSearchParams(window.location.search).get('jackpotcolor');
    if (qs === 'gold' || qs === 'pink') {
      localStorage.setItem(JACKPOT_COLOR_KEY, qs);
    }
    return localStorage.getItem(JACKPOT_COLOR_KEY) !== 'pink';
  } catch {
    // localStorage unavailable (e.g. private-browsing with storage blocked)
    return true;
  }
};

const resolveHotColdShortcuts = (): boolean => {
  try {
    const qs = new URLSearchParams(window.location.search).get('mapshortcuts');
    if (qs === 'enablehotcold' || qs === 'disablehotcold') {
      localStorage.setItem(MAP_SHORTCUTS_KEY, qs);
    }
    return localStorage.getItem(MAP_SHORTCUTS_KEY) === 'enablehotcold';
  } catch {
    // localStorage unavailable (e.g. private-browsing with storage blocked)
    return false;
  }
};

const resolveDrawerActionButtons = (): boolean => {
  try {
    const qs = new URLSearchParams(window.location.search).get(
      'featureFlagDrawerActionButtons'
    );
    if (qs === 'false') {
      sessionStorage.setItem(DRAWER_ACTION_BUTTONS_KEY, 'false');
    } else if (qs === 'true') {
      sessionStorage.removeItem(DRAWER_ACTION_BUTTONS_KEY);
    }
    // Default ON; only an explicit ?featureFlagDrawerActionButtons=false (persisted
    // for the session) disables it as a rollback kill-switch.
    return sessionStorage.getItem(DRAWER_ACTION_BUTTONS_KEY) !== 'false';
  } catch {
    // sessionStorage unavailable (e.g. private-browsing with storage blocked):
    // fall back to the default ON behavior.
    return true;
  }
};

const DETAIL_REPORT_V2_KEY = 'sc_detail_report_v2';

const resolveDetailReportV2 = (): boolean => {
  try {
    const hashSearch = window.location.hash.includes('?')
      ? window.location.hash.split('?')[1]
      : '';
    const search = window.location.search || hashSearch;
    const qs = new URLSearchParams(search).get('detail');
    if (qs === 'true' || qs === 'false') {
      localStorage.setItem(DETAIL_REPORT_V2_KEY, qs);
    }
    return localStorage.getItem(DETAIL_REPORT_V2_KEY) !== 'false';
  } catch {
    return false;
  }
};

const NEARBY_CASINOS_KEY = 'sc_nearby_casinos';

const resolveNearbyCasinos = (): boolean => {
  try {
    const hashSearch = window.location.hash.includes('?')
      ? window.location.hash.split('?')[1]
      : '';
    const search = window.location.search || hashSearch;
    const qs = new URLSearchParams(search).get('nearbycasinos');
    if (qs === 'on' || qs === 'off') {
      localStorage.setItem(NEARBY_CASINOS_KEY, qs);
    }
    return localStorage.getItem(NEARBY_CASINOS_KEY) === 'on';
  } catch {
    return false;
  }
};

const THEME_VERSION_KEY = 'sc_theme_version';
export const DEFAULT_THEME_VERSION: 'v1' | 'v2' = 'v2';

const resolveThemeVersion = (): 'v1' | 'v2' => {
  try {
    const qs = new URLSearchParams(window.location.search).get('explore');
    if (qs === 'v1' || qs === 'v2') {
      localStorage.setItem(THEME_VERSION_KEY, qs);
      return qs;
    }
    const stored = localStorage.getItem(THEME_VERSION_KEY);
    return stored === 'v1' ? 'v1' : DEFAULT_THEME_VERSION;
  } catch {
    return DEFAULT_THEME_VERSION;
  }
};

const getThemeVersion = (): 'v1' | 'v2' =>
  typeof window !== 'undefined' ? resolveThemeVersion() : DEFAULT_THEME_VERSION;


export const featureFlags = {
  jackpotGoldColors: typeof window !== 'undefined' ? resolveJackpotGoldColors() : true,
  hotColdShortcuts: typeof window !== 'undefined' ? resolveHotColdShortcuts() : false,
  drawerActionButtons: typeof window !== 'undefined' ? resolveDrawerActionButtons() : true,
  playlist: true,
  detailReportV2: typeof window !== 'undefined' ? resolveDetailReportV2() : true,
  nearbyCasinos: typeof window !== 'undefined' ? resolveNearbyCasinos() : false,
  themeVersion: getThemeVersion(),
  getThemeVersion,
};
